import { Toaster } from "@/components/ui/toaster";
import { Toaster as Sonner } from "@/components/ui/sonner";
import { TooltipProvider } from "@/components/ui/tooltip";
import { QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { BrowserRouter, Routes, Route, Navigate, useSearchParams, useLocation } from "react-router-dom";
import { ThemeProvider } from "next-themes";
import { lazy, Suspense } from "react";
import { LocaleProvider } from "./i18n/LocaleProvider";
import NotFound from "./pages/NotFound";
import { ErrorBoundary } from "./components/ErrorBoundary";
import { useAuth } from "./hooks/useAuth";
import { BrandingProvider } from "./branding";
import { SchoolProvider } from "./contexts/SchoolContext";
import { SchoolGuard } from "./components/SchoolGuard";

const Admin = lazy(() => import("./pages/Admin"));
const Superadmin = lazy(() => import("./pages/Superadmin"));
const Analytics = lazy(() => import("./pages/Analytics"));
const SchoolsManager = lazy(() => import("./pages/SchoolsManager"));
const Student = lazy(() => import("./pages/Student"));
const StudentDashboard = lazy(() => import("./pages/StudentDashboard"));
const StudentPanel = lazy(() => import("./pages/StudentPanel"));
const StudentCourses = lazy(() => import("./pages/StudentCourses"));
const CourseStudentPreview = lazy(() => import("./pages/CourseStudentPreview"));
const ProducerPanel = lazy(() => import("./pages/ProducerPanel"));
const DebugPanel = lazy(() => import("./components/DebugPanel"));
const AuthPage = lazy(() => import("./components/auth/AuthPage"));
const VerifyCertificate = lazy(() => import("./pages/VerifyCertificate"));
const Index = lazy(() => import("./pages/Index"));
const HomeschoolFlorida = lazy(() => import("./pages/HomeschoolFlorida"));
const CourseLanding = lazy(() => import("./pages/CourseLanding"));
const EbookLanding = lazy(() => import("./pages/EbookLanding"));

const queryClient = new QueryClient();

function ProtectedRoute({ children }: { children: React.ReactNode }) {
  const { isSignedIn, isLoading } = useAuth();
  const [searchParams] = useSearchParams();
  const location = useLocation();

  // Bypass auth for testing: ?bypass=test
  if (searchParams.get('bypass') === 'test') {
    return <>{children}</>;
  }

  // FREE TRIAL: ?trial=1 — usuario novo experimenta 1 aula sem cadastro.
  // Student.tsx detecta e limita acesso + mostra CTA de cadastro.
  if (searchParams.get('trial') === '1') {
    return <>{children}</>;
  }

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" />
      </div>
    );
  }

  if (!isSignedIn) {
    // Preserva a URL original — LoginForm lê e redireciona de volta após o login.
    const returnTo = `${location.pathname}${location.search}${location.hash}`;
    return <Navigate to="/login" replace state={{ from: returnTo }} />;
  }

  return <>{children}</>;
}

const PageLoader = () => (
  <div className="min-h-screen flex items-center justify-center">
    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" />
  </div>
);

const AppRoutes = () => (
  <Suspense fallback={<PageLoader />}>
  <Routes>
    <Route path="/" element={<Index />} />
    <Route path="/homeschool-florida" element={<HomeschoolFlorida />} />
    <Route path="/curso/:slug" element={<CourseLanding />} />
    <Route path="/ebook/:slug" element={<EbookLanding />} />
    <Route path="/homeschool-fl" element={<Navigate to="/homeschool-florida" replace />} />
    <Route
      path="/login"
      element={<AuthPage />}
    />
    <Route path="/admin" element={<Admin />} />
    <Route path="/superadmin" element={<Superadmin />} />
    <Route path="/admin/schools" element={<SchoolsManager />} />
    <Route path="/admin/analytics" element={<Analytics />} />
    <Route
      path="/painel"
      element={
        <ProtectedRoute>
          <ProducerPanel view="dashboard" />
        </ProtectedRoute>
      }
    />
    <Route
      path="/painel/cursos"
      element={
        <ProtectedRoute>
          <ProducerPanel view="courses" />
        </ProtectedRoute>
      }
    />
    <Route
      path="/painel/ebooks"
      element={
        <ProtectedRoute>
          <ProducerPanel view="ebooks" />
        </ProtectedRoute>
      }
    />
    <Route
      path="/painel/cursos/:id"
      element={
        <ProtectedRoute>
          <ProducerPanel view="editor" />
        </ProtectedRoute>
      }
    />
    <Route
      path="/painel/oportunidades"
      element={
        <ProtectedRoute>
          <ProducerPanel view="opportunities" />
        </ProtectedRoute>
      }
    />
    <Route
      path="/painel/clones"
      element={
        <ProtectedRoute>
          <ProducerPanel view="clones" />
        </ProtectedRoute>
      }
    />
    <Route
      path="/painel/alunos"
      element={
        <ProtectedRoute>
          <ProducerPanel view="students" />
        </ProtectedRoute>
      }
    />
    <Route
      path="/painel/paginas"
      element={
        <ProtectedRoute>
          <ProducerPanel view="landing-pages" />
        </ProtectedRoute>
      }
    />
    <Route
      path="/painel/paginas/:id"
      element={
        <ProtectedRoute>
          <ProducerPanel view="landing-editor" />
        </ProtectedRoute>
      }
    />
    <Route
      path="/painel/configuracoes"
      element={
        <ProtectedRoute>
          <ProducerPanel view="settings" />
        </ProtectedRoute>
      }
    />
    <Route
      path="/painel/cursos/:id/preview-aluno"
      element={
        <ProtectedRoute>
          <CourseStudentPreview />
        </ProtectedRoute>
      }
    />
    <Route
      path="/aluno"
      element={
        <SchoolGuard>
          <ProtectedRoute>
            <Student />
          </ProtectedRoute>
        </SchoolGuard>
      }
    />
    <Route
      path="/aluno/cursos"
      element={
        <SchoolGuard>
          <ProtectedRoute>
            <StudentCourses />
          </ProtectedRoute>
        </SchoolGuard>
      }
    />
    <Route
      path="/aluno/dashboard"
      element={
        <SchoolGuard>
          <ProtectedRoute>
            <StudentDashboard />
          </ProtectedRoute>
        </SchoolGuard>
      }
    />
    <Route
      path="/aluno/painel"
      element={
        <SchoolGuard>
          <ProtectedRoute>
            <StudentPanel />
          </ProtectedRoute>
        </SchoolGuard>
      }
    />
    <Route path="/verificar/:code" element={<VerifyCertificate />} />
    <Route path="*" element={<NotFound />} />
  </Routes>
  </Suspense>
);

const AppContent = () => {
  return (
    <QueryClientProvider client={queryClient}>
      <ThemeProvider attribute="class" defaultTheme="dark" enableSystem>
        <TooltipProvider>
          <Toaster />
          <Sonner />
          <BrowserRouter>
            <SchoolProvider>
              {/* LocaleProvider precisa estar DENTRO do BrowserRouter pra
                  acessar useAuth (que internamente usa hooks de router) e
                  sincronizar com profiles.preferred_locale do usuário. */}
              <LocaleProvider>
                <ErrorBoundary>
                  <AppRoutes />
                </ErrorBoundary>
              </LocaleProvider>
            </SchoolProvider>
          </BrowserRouter>
          <DebugPanel />
        </TooltipProvider>
      </ThemeProvider>
    </QueryClientProvider>
  );
};

const App = () => (
  <BrandingProvider>
    <AppContent />
  </BrandingProvider>
);

export default App;
